import { useEffect, useState } from "react";
import { Crown, ShieldAlert, ExternalLink, BellRing } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS = {
  manual: { label: "Suspendue", cls: "bg-red-100 text-red-700" },
  expired: { label: "Abonnement expiré", cls: "bg-red-100 text-red-700" },
  active: { label: "Active", cls: "bg-green-100 text-green-700" },
};

const DURATIONS = [
  { months: 1, label: "1 mois" },
  { months: 3, label: "3 mois" },
  { months: 6, label: "6 mois" },
  { months: 12, label: "1 an" },
  { months: 24, label: "2 ans" },
];
const ALERT_PRESETS = [60, 30, 15, 7, 3, 1, 0];

// Fin incluse : souscrit le 01/01 pour 1 an → fin le 31/12.
function addMonths(iso, months) {
  const [y, m, d] = iso.split("-").map(Number);
  const end = new Date(Date.UTC(y, m - 1 + months, d));
  if (end.getUTCDate() !== d) end.setUTCDate(0); // 31/01 + 1 mois → dernier jour de février
  else end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const inputCls = "w-full border border-stone bg-white px-3 py-2.5 text-sm text-ink rounded-sm focus:outline-none focus:border-gold";

export default function AdminSubscription() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === "superadmin") api.get("/superadmin/subscription").then(({ data }) => setCfg(data)).catch(() => {});
  }, [user]);

  if (user?.role !== "superadmin") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-copper" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl text-ink">Accès réservé</h1>
        <p className="text-graphite max-w-sm">Ce module est réservé aux super administrateurs.</p>
      </div>
    );
  }

  if (!cfg) return <div className="h-64 bg-white border border-stone animate-pulse rounded-sm" />;

  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setSaving(true);
    try {
      const { blocked_reason, days_left, ...payload } = cfg;
      const { data } = await api.put("/superadmin/subscription", {
        ...payload,
        started_at: payload.started_at || "",
        expires_at: payload.expires_at || "",
      });
      setCfg(data);
      toast.success(data.blocked_reason ? "Enregistré — la boutique est suspendue" : "Enregistré — la boutique est active");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const status = STATUS[cfg.blocked_reason || "active"];
  const applyDuration = (months) => {
    const start = cfg.started_at || todayIso();
    setCfg((c) => ({ ...c, started_at: start, expires_at: addMonths(start, months) }));
  };
  const toggleAlertDay = (d) =>
    setCfg((c) => {
      const days = c.alert_days || [];
      return { ...c, alert_days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => b - a) };
    });
  const left = cfg.days_left;

  return (
    <div data-testid="admin-subscription" className="max-w-3xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
          <Crown className="h-4 w-4 text-gold" /> Super admin
        </p>
        <h1 className="font-serif text-4xl text-ink tracking-tight">Abonnement</h1>
        <p className="text-graphite mt-2">
          Une boutique suspendue ferme uniquement le site (vitrine, panier, commandes) pour tout le monde.
          La connexion et le back-office restent accessibles aux admins et super admins.
        </p>
      </div>

      <section className="bg-white border border-stone rounded-sm p-5 sm:p-6 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <h2 className="font-serif text-xl text-ink">État</h2>
          <span data-testid="subscription-status" className={`text-xs uppercase tracking-[0.1em] px-2.5 py-1 rounded-sm ${status.cls}`}>
            {status.label}
          </span>
        </div>
        <label className="flex items-center justify-between gap-4 py-3 border-b border-stone">
          <span>
            <span className="block text-sm text-ink">Suspendre la boutique maintenant</span>
            <span className="block text-xs text-ash">Blocage manuel, indépendamment de la date de fin.</span>
          </span>
          <Switch data-testid="subscription-suspended" checked={!!cfg.suspended} onCheckedChange={set("suspended")} className="data-[state=checked]:bg-red-600" />
        </label>
        <div className="pt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-ink mb-1.5" htmlFor="sub-start">Date de souscription</label>
            <input id="sub-start" type="date" data-testid="subscription-started" value={cfg.started_at || ""} onChange={set("started_at")} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-ink mb-1.5" htmlFor="sub-expires">Fin de l&apos;abonnement</label>
            <div className="flex gap-2 items-center">
              <input id="sub-expires" type="date" data-testid="subscription-expires" value={cfg.expires_at || ""} min={cfg.started_at || undefined} onChange={set("expires_at")} className={inputCls} />
              {cfg.expires_at && (
                <button type="button" onClick={() => set("expires_at")("")} className="text-xs uppercase tracking-[0.1em] text-ash hover:text-ink px-2 py-2">
                  Retirer
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-ash mr-1">Durée payée :</span>
          {DURATIONS.map((d) => (
            <button
              key={d.months}
              type="button"
              onClick={() => applyDuration(d.months)}
              data-testid={`subscription-duration-${d.months}`}
              className="text-xs px-3 py-1.5 border border-stone rounded-sm text-graphite hover:border-gold hover:text-ink"
            >
              {d.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-ash mt-2">
          Date de fin incluse : la boutique se bloque automatiquement le lendemain. Vide = sans échéance.
          {left != null && left >= 0 && <strong className="text-ink"> {left === 0 ? "Dernier jour aujourd'hui." : `${left} jour${left > 1 ? "s" : ""} restant${left > 1 ? "s" : ""}.`}</strong>}
        </p>
      </section>

      <section className="bg-white border border-stone rounded-sm p-5 sm:p-6 mb-6">
        <h2 className="font-serif text-xl text-ink mb-1 flex items-center gap-2">
          <BellRing className="h-5 w-5 text-copper" strokeWidth={1.5} /> Alertes avant échéance
        </h2>
        <p className="text-xs text-ash mb-4">
          Aux seuils choisis, un bandeau s&apos;affiche sur le tableau de bord des admins et super admins, et un e-mail
          leur est envoyé (un seul par seuil), puis un dernier le jour où la boutique est suspendue.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {ALERT_PRESETS.map((d) => {
            const on = (cfg.alert_days || []).includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleAlertDay(d)}
                data-testid={`subscription-alert-${d}`}
                className={`text-xs px-3 py-1.5 border rounded-sm ${on ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:border-gold"}`}
              >
                {d === 0 ? "Jour J" : `J-${d}`}
              </button>
            );
          })}
        </div>
        <label className="flex items-center justify-between gap-4 pt-3 border-t border-stone">
          <span>
            <span className="block text-sm text-ink">Envoyer les alertes par e-mail</span>
            <span className="block text-xs text-ash">Via le module Communication (clé Resend requise).</span>
          </span>
          <Switch data-testid="subscription-alert-emails" checked={!!cfg.alert_emails_enabled} onCheckedChange={set("alert_emails_enabled")} className="data-[state=checked]:bg-gold" />
        </label>
      </section>

      <section className="bg-white border border-stone rounded-sm p-5 sm:p-6 mb-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-serif text-xl text-ink">Page de suspension</h2>
          <a href="/boutique-suspendue" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-[0.1em] text-copper hover:text-ink inline-flex items-center gap-1">
            Aperçu <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div>
          <label className="block text-sm text-ink mb-1.5" htmlFor="sub-shop">Nom de la boutique</label>
          <input id="sub-shop" value={cfg.shop_name} onChange={set("shop_name")} className={inputCls} placeholder="ex. assurance" />
        </div>
        <div>
          <label className="block text-sm text-ink mb-1.5" htmlFor="sub-title">Titre</label>
          <input id="sub-title" value={cfg.title} onChange={set("title")} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm text-ink mb-1.5" htmlFor="sub-message">Message</label>
          <textarea id="sub-message" rows={3} value={cfg.message} onChange={set("message")} className={inputCls} />
        </div>

        <div className="pt-2 border-t border-stone">
          <label className="flex items-center justify-between gap-4 py-3">
            <span className="text-sm text-ink">Afficher le téléphone</span>
            <Switch data-testid="subscription-show-phone" checked={!!cfg.show_phone} onCheckedChange={set("show_phone")} className="data-[state=checked]:bg-gold" />
          </label>
          <input type="tel" data-testid="subscription-phone" value={cfg.phone} onChange={set("phone")} disabled={!cfg.show_phone} className={`${inputCls} disabled:opacity-50`} placeholder="+33 6 00 00 00 00" />
        </div>
        <div>
          <label className="flex items-center justify-between gap-4 py-3">
            <span className="text-sm text-ink">Afficher l&apos;e-mail</span>
            <Switch data-testid="subscription-show-email" checked={!!cfg.show_email} onCheckedChange={set("show_email")} className="data-[state=checked]:bg-gold" />
          </label>
          <input type="email" data-testid="subscription-email" value={cfg.email} onChange={set("email")} disabled={!cfg.show_email} className={`${inputCls} disabled:opacity-50`} placeholder="support@exemple.com" />
        </div>
      </section>

      <button
        onClick={save}
        disabled={saving}
        data-testid="subscription-save"
        className="bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] rounded-sm hover:bg-graphite disabled:opacity-50"
      >
        {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}
